import axios from 'axios';
import { visitorId } from '../utils/trackPageView';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Services API
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/password', data),
};

export const subscriberService = {
  getAll: (params) => api.get('/subscribers', { params }),
  getPending: () => api.get('/subscribers/pending'),
  getById: (id) => api.get(`/subscribers/${id}`),
  create: (data) => api.post('/subscribers', data),
  update: (id, data) => api.put(`/subscribers/${id}`, data),
  validate: (id) => api.put(`/subscribers/${id}/validate`),
  block: (id) => api.put(`/subscribers/${id}/block`),
  delete: (id) => api.delete(`/subscribers/${id}`),
};

export const proverbService = {
  getAll: (params) => api.get('/proverbs', { params }),
  getStats: (year) => api.get('/proverbs/stats', { params: { year } }),
  getById: (id) => api.get(`/proverbs/${id}`),
  create: (data) => api.post('/proverbs', data),
  update: (id, data) => api.put(`/proverbs/${id}`, data),
  delete: (id) => api.delete(`/proverbs/${id}`),
  exportFile: (format) => api.get('/proverbs/export', { params: { format }, responseType: 'blob' }),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/proverbs/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const ephemerisService = {
  getAll: (params) => api.get('/ephemeris', { params }),
  getToday: () => api.get('/ephemeris/today'),
  getByDate: (date) => api.get(`/ephemeris/date/${date}`),
  getById: (id) => api.get(`/ephemeris/${id}`),
  generate: (date, languageId) => api.post('/ephemeris/generate', { date, language_id: languageId }),
  generateYear: (year, languageId) => api.post('/ephemeris/generate-year', { year, language_id: languageId }),
  getImageUrl: (id, type = 'frame') => `${API_URL}/ephemeris/${id}/image/${type}`,
};

export const whatsappService = {
  getMessages: (params) => api.get('/whatsapp/messages', { params }),
  getStats: (params) => api.get('/whatsapp/stats', { params }),
  send: (subscriberId, ephemerisId) => api.post('/whatsapp/send', { 
    subscriber_id: subscriberId, 
    ephemeride_id: ephemerisId 
  }),
  sendDaily: (ephemerisId) => api.post('/whatsapp/send-daily', { ephemeride_id: ephemerisId }),
  getSubscriberHistory: (id, limit) => api.get(`/whatsapp/subscriber/${id}/history`, { params: { limit } }),
  retry: (id) => api.post(`/whatsapp/retry/${id}`),
};

export const dashboardService = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentSubscriptions: () => api.get('/dashboard/recent-subscriptions'),
  getMessageStats: (days) => api.get('/dashboard/message-stats', { params: { days } }),
  getProverbUsage: (year) => api.get('/dashboard/proverb-usage', { params: { year } }),
};

export const settingsService = {
  getAll: () => api.get('/settings'),
  get: (key) => api.get(`/settings/${key}`),
  update: (key, value, description) => api.put(`/settings/${key}`, { value, description }),
  updateMultiple: (settings) => api.put('/settings', settings),
  getLanguages: () => api.get('/settings/languages/all'),
  getWeekdays: () => api.get('/settings/weekdays/all'),
  getMonths: () => api.get('/settings/months/all'),
};

export const dictionaryService = {
  getLetters: () => api.get('/dictionary/letters'),
  getByLetter: (letter, params) => api.get(`/dictionary/letter/${letter}`, { params }),
  getIllustrated: (data) => api.post('/dictionary/illustrated', data),
  // track=1 : la recherche est comptee dans les statistiques (anonyme, voir backend/src/services/searchLog.service.js)
  search: (q, params) => api.get('/dictionary/search', { params: { q, track: 1, ...params }, headers: { 'X-Visitor-Id': visitorId() || '' } }),
  setVerified: (id, verified) => api.patch(`/dictionary/${id}/verify`, { verified }),
  suggestWord: (data) => api.post('/dictionary/suggest', data),
  autocomplete: (prefix) => api.get('/dictionary/autocomplete', { params: { prefix } }),
  checkWords: (words) => api.post('/dictionary/check-words', { words }),
  getSuggestions: (params) => api.get('/dictionary/suggestions', { params }),
  updateSuggestionStatus: (id, status) => api.patch(`/dictionary/suggestions/${id}/status`, { status }),
  reportCorrection: (entryId, data) => api.post(`/dictionary/${entryId}/report-correction`, data),
  getCorrections: (params) => api.get('/dictionary/corrections', { params }),
  updateCorrectionStatus: (id, status) => api.patch(`/dictionary/corrections/${id}/status`, { status }),
  updateEntry: (id, data) => api.patch(`/dictionary/${id}`, data),
  proposeChanges: (entryId, changes, note) => api.post(`/dictionary/${entryId}/propose`, { changes, note }),
  getProposals: (params) => api.get('/dictionary/proposals', { params }),
  acceptProposals: (ids, force) => api.post('/dictionary/proposals/accept', { ids, force }),
  refuseProposals: (ids, reason) => api.post('/dictionary/proposals/refuse', { ids, reason }),
  addSuggestedWord: (suggestionId, data) => api.post(`/dictionary/suggestions/${suggestionId}/add`, data),
  exportExcel: (letter, unverified) => api.get('/dictionary/export', { params: { letter: letter || undefined, unverified: unverified ? 1 : undefined }, responseType: 'blob' }),
  importExcel: (file, reviewer) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('reviewer', reviewer || '');
    return api.post('/dictionary/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getChanges: (params) => api.get('/dictionary/changes', { params }),
  revertChange: (id, force) => api.post(`/dictionary/changes/${id}/revert`, { force }),
};

export const userService = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
};

export const donationService = {
  get: () => api.get('/donations'),
  save: (data) => api.put('/donations', data),
};

export const sponsorService = {
  getAll: () => api.get('/sponsors'),
  create: (data) => api.post('/sponsors', data),
  update: (id, data) => api.put(`/sponsors/${id}`, data),
  remove: (id) => api.delete(`/sponsors/${id}`),
};

export const audioService = {
  worklist: (params) => api.get('/audio/worklist', { params }),
  // Corps brut (le fichier enregistre) : le serveur lit les premiers octets pour reconnaitre le format.
  upload: (letter, duala, blob) => api.post('/audio', blob, { params: { letter, duala }, headers: { 'Content-Type': blob.type || 'application/octet-stream' }, transformRequest: [(d) => d] }),
  remove: (id) => api.delete(`/audio/${id}`),
};

export const statsService = {
  getSummary: (days) => api.get('/stats/summary', { params: { days } }),
};

export const broadcastService = {
  getRecipients: () => api.get('/broadcasts/recipients'),
  getPacing: () => api.get('/broadcasts/pacing'),
  setPacing: (batchSize, pauseSeconds) => api.put('/broadcasts/pacing', { batch_size: batchSize, pause_seconds: pauseSeconds }),
  preview: (data) => api.post('/broadcasts/preview', data),
  sendTest: (texts, language) => api.post('/broadcasts/test', { texts, language }),
  create: (data) => api.post('/broadcasts', data),
  getAll: () => api.get('/broadcasts'),
  getById: (id) => api.get(`/broadcasts/${id}`),
  cancel: (id) => api.post(`/broadcasts/${id}/cancel`),
  resume: (id) => api.post(`/broadcasts/${id}/resume`),
};

export const archiveService = {
  getDaily: (date) => api.get('/archives/daily', { params: { date } }),
  getImageUrl: (relativeUrl) => `${API_URL.replace('/api', '')}${relativeUrl}`,
};

export default api;
